import { useState, useMemo, useRef, useCallback } from "react";
import { submitKangstudyForm, formDataToRecord } from "@/lib/submit-form";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { reports, reportTypes } from "@/mocks/reports";
import { motion, AnimatePresence } from "motion/react";
import ReportSamplePreview from "@/components/feature/ReportSamplePreview";
import SuccessSlider from "@/pages/reports/components/SuccessSlider";

export default function ReportsPage() {
  const [activeType, setActiveType] = useState("전체");
  const [sortBy, setSortBy] = useState("latest");
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // PDF download form state
  const [downloadEmail, setDownloadEmail] = useState("");
  const [downloadStatus, setDownloadStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [downloadError, setDownloadError] = useState("");
  const downloadFormRef = useRef<HTMLFormElement>(null);

  const filtered = useMemo(() => {
    let items = [...reports];
    if (activeType !== "전체") {
      items = items.filter((r) => r.type === activeType);
    }
    switch (sortBy) {
      case "popular":
        items.sort((a, b) => b.sales - a.sales);
        break;
      case "priceLow":
        items.sort((a, b) => a.price - b.price);
        break;
      case "priceHigh":
        items.sort((a, b) => b.price - a.price);
        break;
      default:
        break;
    }
    return items;
  }, [activeType, sortBy]);

  const report = selectedReport
    ? reports.find((r) => r.id === selectedReport)
    : null;

  const isFree = activeType === "무료";
  const isReportFree = report?.price === 0;

  const typeIcon = (type: string) => {
    switch (type) {
      case "물건분석":
        return "ri-file-search-line";
      case "지역분석":
        return "ri-map-pin-line";
      case "체크리스트":
        return "ri-task-line";
      case "무료":
        return "ri-gift-line";
      default:
        return "ri-file-text-line";
    }
  };

  const typeColor = (type: string) => {
    switch (type) {
      case "물건분석":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "지역분석":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "체크리스트":
        return "bg-rose-50 text-rose-700 border-rose-200";
      case "무료":
        return "bg-green-50 text-green-700 border-green-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const handleDownloadSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setDownloadError("");

    const formElement = downloadFormRef.current;
    if (!formElement) return;

    const formData = new FormData(formElement);
    const honeypotValue = (formData.get("company_alt") as string || "").trim();
    if (honeypotValue !== "") {
      setDownloadStatus("success");
      setTimeout(() => setDownloadStatus("idle"), 2000);
      return;
    }

    setDownloadStatus("loading");

    try {
      const { ok, error } = await submitKangstudyForm(
        "report_download",
        formDataToRecord(formData),
      );
      if (ok) {
        setDownloadStatus("success");
        setDownloadEmail("");
      } else {
        setDownloadError(error);
        setDownloadStatus("error");
        setTimeout(() => setDownloadStatus("idle"), 3000);
      }
    } catch {
      setDownloadError("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
      setDownloadStatus("error");
      setTimeout(() => setDownloadStatus("idle"), 3000);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />

      {/* Detail Modal */}
      <AnimatePresence>
        {report && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[10vh] bg-black/40 overflow-y-auto"
            onClick={() => setSelectedReport(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="bg-white rounded-2xl max-w-[560px] w-full max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative h-56 overflow-hidden rounded-t-2xl">
                <img loading="lazy" decoding="async"
                  src={report.thumbnail}
                  alt={report.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="absolute top-4 right-4 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-[#1C1917]"></i>
                </button>
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${typeColor(report.type)}`}>
                    {report.type}
                  </span>
                  {report.valueTag && (
                    <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-[#1C1917]/80 text-white">
                      {report.valueTag}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-6">
                <h2 className="text-lg font-bold text-[#1C1917] mb-2 leading-snug">{report.title}</h2>
                <p className="text-sm text-[#8C8780] mb-5 leading-relaxed">{report.description}</p>

                <div className="flex flex-wrap items-center gap-3 mb-5">
                  {report.tags.map((tag, i) => (
                    <span key={i} className="px-2.5 py-1 text-xs bg-[#FAFAF8] text-[#5C5650] rounded-full border border-[#E8E5E0]">
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-[#FAFAF8] rounded-xl">
                  <div className="text-center">
                    <p className="text-xs text-[#8C8780] mb-1">페이지</p>
                    <p className="text-sm font-bold text-[#1C1917]">{report.pages}p</p>
                  </div>
                  <div className="text-center border-x border-[#E8E5E0]">
                    <p className="text-xs text-[#8C8780] mb-1">판매량</p>
                    <p className="text-sm font-bold text-[#1C1917]">{report.sales.toLocaleString()}건</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#8C8780] mb-1">작성자</p>
                    <p className="text-sm font-bold text-[#1C1917]">{report.author}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-6">
                  <span className="text-2xl font-bold text-[#1C1917]">
                    {report.price === 0 ? "무료" : `${report.price.toLocaleString()}원`}
                  </span>
                  <span className="text-xs text-[#B8B3AE]">{report.createdAt} 등록</span>
                </div>

                <div className="flex gap-3 mb-6">
                  <button
                    onClick={() => setSelectedReport(null)}
                    className="flex-1 px-5 py-3 text-sm font-medium border border-[#E8E5E0] text-[#5C5650] rounded-full hover:border-[#C4956A] hover:text-[#C4956A] transition-all whitespace-nowrap cursor-pointer"
                  >
                    닫기
                  </button>
                  <button className="flex-[2] px-5 py-3 text-sm font-semibold bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-shopping-cart-line mr-2"></i>
                    {isReportFree ? "무료 다운로드" : "구매하기"}
                  </button>
                </div>

                {/* Post-Purchase CTA Section */}
                <div className="border-t border-[#F0EDE8] pt-5 space-y-4">
                  <p className="text-xs font-medium text-[#8C8780]">이 리포트와 함께하면 좋은 서비스</p>
                  <div className="grid grid-cols-2 gap-3">
                    <a
                      href="/consultation"
                      className="flex items-center gap-3 p-4 rounded-xl border border-[#E8E5E0] hover:border-[#C4956A]/40 hover:bg-[#C4956A]/5 transition-all group"
                    >
                      <div className="w-9 h-9 rounded-lg bg-[#C4956A]/10 flex items-center justify-center flex-shrink-0">
                        <i className="ri-customer-service-2-line text-[#C4956A]"></i>
                      </div>
                      <div className="text-left min-w-0">
                        <p className="text-xs font-bold text-[#1C1917] group-hover:text-[#C4956A] transition-colors whitespace-nowrap">1:1 상담 신청</p>
                        <p className="text-[10px] text-[#8C8780] whitespace-nowrap">전문가 맞춤 컨설팅</p>
                      </div>
                    </a>
                    <a
                      href="/lectures"
                      className="flex items-center gap-3 p-4 rounded-xl border border-[#E8E5E0] hover:border-[#C4956A]/40 hover:bg-[#C4956A]/5 transition-all group"
                    >
                      <div className="w-9 h-9 rounded-lg bg-[#C4956A]/10 flex items-center justify-center flex-shrink-0">
                        <i className="ri-play-circle-line text-[#C4956A]"></i>
                      </div>
                      <div className="text-left min-w-0">
                        <p className="text-xs font-bold text-[#1C1917] group-hover:text-[#C4956A] transition-colors whitespace-nowrap">강의 보러가기</p>
                        <p className="text-[10px] text-[#8C8780] whitespace-nowrap">체계적인 커리큘럼</p>
                      </div>
                    </a>
                  </div>

                  {/* PDF Download Form */}
                  <div className="bg-[#FAFAF8] rounded-xl p-4 border border-[#E8E5E0]">
                    <div className="flex items-center gap-2 mb-3">
                      <i className="ri-file-pdf-line text-rose-500"></i>
                      <p className="text-xs font-bold text-[#1C1917]">PDF 샘플 받아보기</p>
                    </div>
                    <p className="text-[10px] text-[#8C8780] mb-3">이메일을 입력하시면 리포트 샘플 PDF를 보내드립니다</p>
                    <form ref={downloadFormRef} onSubmit={handleDownloadSubmit} data-readdy-form className="flex gap-2">
                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="form_aux_wrapper"
                      />
                      <input
                        type="email"
                        name="email"
                        value={downloadEmail}
                        onChange={(e) => setDownloadEmail(e.target.value)}
                        placeholder="이메일 주소 입력"
                        required
                        className="flex-1 px-3 py-2 text-xs border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors"
                      />
                      <button
                        type="submit"
                        disabled={downloadStatus === "loading" || !downloadEmail.trim()}
                        className="px-4 py-2 text-xs font-semibold bg-[#1C1917] text-white rounded-lg hover:bg-[#C4956A] transition-all whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {downloadStatus === "loading" ? (
                          <i className="ri-loader-4-line animate-spin"></i>
                        ) : downloadStatus === "success" ? (
                          <span className="text-emerald-400"><i className="ri-check-line"></i></span>
                        ) : (
                          "받기"
                        )}
                      </button>
                    </form>
                    {downloadError && (
                      <p className="text-[10px] text-red-500 mt-2">{downloadError}</p>
                    )}
                    {downloadStatus === "success" && (
                      <p className="text-[10px] text-emerald-600 mt-2">이메일로 샘플 PDF를 발송했습니다!</p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="mb-8"
          >
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-file-text-line text-[#C4956A]"></i>
              <span className="text-xs font-medium text-[#C4956A]">리포트</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1C1917] mb-2">
              투자 분석 리포트
            </h1>
            <p className="text-sm text-[#8C8780]">
              현장 전문가가 작성한 물건분석, 시장분석, 실전 체크리스트로 더 안전한 투자 결정을 내리세요
            </p>
          </motion.div>

          {/* Sample Preview + Success Slider */}
          <div className="flex flex-col lg:flex-row gap-5 mb-8">
            {/* Sample Preview Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
              className="flex-1 bg-white rounded-xl border border-[#E8E5E0] p-5 md:p-6 flex items-center gap-5"
            >
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-[#C4956A]/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img loading="lazy" decoding="async"
                  src="https://img.echotwin.kr/kangstudy/d02af98988c1aa1b.jpg"
                  alt="샘플 리포트 미리보기"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base md:text-lg font-bold text-[#1C1917] mb-1">
                  리포트 샘플 미리보기
                </h3>
                <p className="text-sm text-[#8C8780] mb-4 leading-relaxed">
                  전문가가 실제로 작성한 물건분석 리포트의 샘플을 확인해보세요. 권리분석, 시세 비교, 예상 수익률까지 꼼꼼하게 담았습니다.
                </p>
                <button
                  onClick={() => setShowPreview(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1C1917] text-white text-sm font-semibold rounded-full hover:bg-[#C4956A] transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-eye-line"></i>
                  샘플 리포트 보기
                </button>
              </div>
            </motion.div>

            {/* Success Slider */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
              className="lg:w-[380px]"
            >
              <SuccessSlider />
            </motion.div>
          </div>

          {/* Type Filter Tabs */}
          <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
            {reportTypes.map((t) => (
              <button
                key={t}
                onClick={() => setActiveType(t)}
                className={`px-4 py-2 text-sm rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  activeType === t
                    ? "bg-[#1C1917] text-white font-medium"
                    : "bg-white border border-[#E8E5E0] text-[#5C5650] hover:border-[#C4956A] hover:text-[#C4956A]"
                } ${t === "무료" ? "relative" : ""}`}
              >
                {t === "전체" ? "전체" : t}
                {t === "무료" && (
                  <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-rose-500 rounded-full flex items-center justify-center">
                    <span className="block w-1 h-1 bg-white rounded-full"></span>
                  </span>
                )}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-[#8C8780] whitespace-nowrap">정렬</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 text-sm border border-[#E8E5E0] rounded-lg bg-white text-[#5C5650] focus:outline-none focus:border-[#C4956A] cursor-pointer"
              >
                <option value="latest">최신순</option>
                <option value="popular">인기순</option>
                <option value="priceLow">가격 낮은순</option>
                <option value="priceHigh">가격 높은순</option>
              </select>
            </div>
          </div>

          {/* Lead Magnet Banner - shown only for "무료" tab */}
          <AnimatePresence>
            {isFree && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" as const }}
                className="overflow-hidden mb-6"
              >
                <div className="bg-gradient-to-r from-[#1C1917] to-[#3D3833] rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
                  <div className="flex-1 text-center md:text-left">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 rounded-full mb-3">
                      <i className="ri-gift-line text-amber-400 text-xs"></i>
                      <span className="text-xs text-amber-400 font-medium">무료 다운로드</span>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-white mb-2">
                      지금 무료 리포트를 다운로드하고<br />
                      <span className="text-amber-400">경매 투자의 첫걸음</span>을 시작하세요
                    </h3>
                    <p className="text-sm text-[#B8B3AE] mb-0 md:mb-0 leading-relaxed">
                      이메일만 입력하시면 경매 투자 입문 가이드와 2026 시장전망 리포트를 무료로 보내드립니다.
                    </p>
                  </div>
                  <div className="w-full md:w-[320px] flex-shrink-0">
                    <form ref={downloadFormRef} onSubmit={handleDownloadSubmit} data-readdy-form className="flex flex-col sm:flex-row md:flex-col gap-2">
                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="form_aux_wrapper"
                      />
                      <input
                        type="email"
                        name="email"
                        value={downloadEmail}
                        onChange={(e) => setDownloadEmail(e.target.value)}
                        placeholder="이메일 주소를 입력하세요"
                        required
                        className="flex-1 px-4 py-2.5 text-sm border border-white/20 bg-white/10 text-white placeholder:text-[#B8B3AE] rounded-lg focus:outline-none focus:border-amber-400 transition-colors"
                      />
                      <button
                        type="submit"
                        disabled={downloadStatus === "loading" || !downloadEmail.trim()}
                        className="px-6 py-2.5 text-sm font-semibold bg-amber-500 text-[#1C1917] rounded-lg hover:bg-amber-400 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {downloadStatus === "loading" ? (
                          <><i className="ri-loader-4-line animate-spin mr-1.5"></i>전송 중...</>
                        ) : downloadStatus === "success" ? (
                          <><i className="ri-check-line mr-1.5"></i>전송 완료!</>
                        ) : (
                          "무료로 받기"
                        )}
                      </button>
                    </form>
                    {downloadError && (
                      <p className="text-xs text-red-400 mt-2">{downloadError}</p>
                    )}
                    <p className="text-[10px] text-[#8C8780] mt-2 text-center md:text-left">
                      개인정보는 발송 목적으로만 사용되며 이후 즉시 파기됩니다
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <p className="text-xs text-[#8C8780] mb-5">
            총 <strong className="text-[#1C1917]">{filtered.length}</strong>개의
            리포트
          </p>

          {/* Report Grid */}
          <motion.div
            layout
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {filtered.map((item) => (
              <motion.button
                key={item.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" as const }}
                onClick={() => setSelectedReport(item.id)}
                whileHover={{ y: -4 }}
                className="group bg-white rounded-xl border border-[#E8E5E0] overflow-hidden hover:border-[#C4956A]/30 transition-all text-left cursor-pointer"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img loading="lazy" decoding="async"
                    src={item.thumbnail}
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${typeColor(item.type)}`}>
                      {item.type}
                    </span>
                    {/* Value Badge */}
                    {item.valueTag && (
                      <span className="px-2 py-1 text-[10px] font-semibold bg-[#1C1917]/75 text-white rounded-full backdrop-blur-sm">
                        {item.valueTag}
                      </span>
                    )}
                  </div>
                  <div className="absolute bottom-3 right-3">
                    <span className={`px-3 py-1 text-sm font-bold rounded-full ${
                      item.price === 0
                        ? "bg-emerald-500 text-white"
                        : "bg-white/90 text-[#1C1917]"
                    }`}>
                      {item.price === 0 ? "무료" : `${item.price.toLocaleString()}원`}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="text-base font-bold text-[#1C1917] mb-2 line-clamp-2 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#8C8780] mb-4 line-clamp-2 leading-relaxed">
                    {item.description}
                  </p>

                  {/* Achievement highlight */}
                  {item.achievement && (
                    <div className="flex items-center gap-1.5 mb-3 px-3 py-2 bg-[#FAFAF8] rounded-lg">
                      <i className="ri-checkbox-circle-fill text-emerald-500 text-xs"></i>
                      <span className="text-[11px] font-medium text-[#5C5650]">{item.achievement}</span>
                    </div>
                  )}

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {item.tags.map((tag, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs bg-[#FAFAF8] text-[#5C5650] rounded-full">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Meta */}
                  <div className="flex items-center justify-between text-xs text-[#8C8780] pt-3 border-t border-[#F0EDE8]">
                    <span className="flex items-center gap-1">
                      <i className={typeIcon(item.type)}></i>
                      {item.pages}페이지
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-download-line"></i>
                      {item.sales.toLocaleString()}건
                    </span>
                    <span className="text-[#B8B3AE]">{item.createdAt}</span>
                  </div>
                </div>
              </motion.button>
            ))}
          </motion.div>

          {/* Empty State */}
          {filtered.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#C4956A]/10 flex items-center justify-center">
                <i className="ri-file-text-line text-2xl text-[#C4956A]"></i>
              </div>
              <p className="text-[#5C5650] font-medium mb-1">
                해당하는 리포트가 없습니다
              </p>
              <p className="text-sm text-[#8C8780]">
                필터를 변경하여 다른 리포트를 찾아보세요
              </p>
            </div>
          )}

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="mt-16 bg-[#1C1917] rounded-2xl p-8 md:p-10 text-center"
          >
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#C4956A]/20 flex items-center justify-center">
              <i className="ri-lightbulb-line text-2xl text-[#C4956A]"></i>
            </div>
            <h3 className="text-lg md:text-xl font-bold text-white mb-3">
              맞춤형 물건분석 리포트가 필요하신가요?
            </h3>
            <p className="text-sm text-[#B8B3AE] mb-6 max-w-[480px] mx-auto leading-relaxed">
              관심 있는 경매 물건을 알려주시면 전문가가 직접 분석한 맞춤 리포트를 제공해드립니다. 1:1 상담을 통해 더 자세한 투자 전략도 상담받으세요.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="/consultation"
                className="w-full sm:w-auto px-8 py-3 bg-[#C4956A] text-white text-sm font-semibold rounded-full hover:bg-[#B08050] transition-all whitespace-nowrap text-center"
              >
                <i className="ri-customer-service-2-line mr-2"></i>
                상담 신청하기
              </a>
            </div>
          </motion.div>
        </div>
      </main>

      <ReportSamplePreview isOpen={showPreview} onClose={() => setShowPreview(false)} />

      <Footer />
    </div>
  );
}